import sortBy from 'lodash/sortBy'
import dayjs from 'dayjs'
import {
  USER_COUPONS_PROMOS,
  ALIGNMENT_SERVICE,
  SIX_MONTHS_FINANCING_MODAL_ID,
  TWELVE_MONTHS_FINANCING_MODAL_ID,
  SIX_MONTHS
} from './constants'

export const getDividedPromotionsList = (appliedPromotions = []) =>
  appliedPromotions && appliedPromotions.reduce((acc, item) => {
    const promotionType = item.isUserAppliedPromotion ? USER_COUPONS_PROMOS : item.promotionType

    if (!acc[promotionType]) {
      acc[promotionType] = []
    }

    acc[promotionType].push(item)

    return acc
  }, {})

export const getFreeAlignmentCheckBlockState = ({
  quotedItemAndServices: {
    services = []
  } = {}
} = {}) =>
  services && services.length !== 0 &&
    services.some(({ serviceId }) => serviceId === ALIGNMENT_SERVICE)

export const getItemWithFilteredServices = ({
  quotedItemAndServices: {
    services = [],
    ...restItems
  },
  freeAligmentTooltip = {}
} = {}) => ({
  ...restItems,
  services: services && services.filter(service => service.commerceItemId !== freeAligmentTooltip.commerceItemId)
})

export const getProductsWithChangedPrice = ({ products = []}) =>
  products && products.filter(({ hasPriceChanged, price }) => hasPriceChanged && parseFloat(price) > 0)

export const sortProducts = products => sortBy(products, 'isActive')

export const injectDynamicVerbiage = ({
  paymentOptionsBannerTemplate,
  estimatedTotal,
  monthlyPaymentAmount,
  paymentOption,
  threePaymentsValue
}) => paymentOptionsBannerTemplate
  .replace(/ESTIMATEDTOTAL/g, String(estimatedTotal))
  .replace(/PAYMENTAMOUNT/g, String(monthlyPaymentAmount))
  .replace(/PAYMENTOPTION/g, paymentOption)
  .replace(/THREEPAYMENTS/g, threePaymentsValue)
  .replace(/MODALOPTION/g, paymentOption === SIX_MONTHS ?
    SIX_MONTHS_FINANCING_MODAL_ID :
    TWELVE_MONTHS_FINANCING_MODAL_ID)

export const getQuoteServicesList = services =>
  services.map(({ name }) => name.trim()).join(', ')

/**
 * @param {Array} skusWithPreviousAvailability
 * @param {Array} skusAddedToCart
 * @param {Array} quotedItemAndServices
 * @param {Array} skusWithChangedAvailability
 * @returns {Array}
 * In this function we compare the skus old availability with the new availability and if there is a diff
 * then push those skus into the skusWithChangedAvailability and update the properties of
 * those skus in localstorage as well.
 * We also get the necessary properties of those particular changed skus from the products array as well.
 */
export const getSkusWithChangedAvailability = (
  skusWithPreviousAvailability,
  skusAddedToCart,
  quotedItemAndServices,
  skusWithChangedAvailability
) => {
  const oldAvailabilityProducts = skusWithPreviousAvailability
  oldAvailabilityProducts.forEach((product, index) => {
    const updatedSku = skusAddedToCart.find(sku => sku.partNo === product.partNo &&
      ((sku.availableAsOf || product.availableAsOf) !== '') &&
      dayjs(sku.availableAsOf, 'MM-DD-YYYY').get('date') !== dayjs(product.availableAsOf, 'MM-DD-YYYY').get('date'))

    if (updatedSku) {
      const updatedProperties = {
        partNo: updatedSku.partNo,
        availableAsOf: updatedSku.availableAsOf
      }
      const { products = []} = quotedItemAndServices
      const product = products?.length ? products.find(item => item.bvId === updatedSku.partNo) : {}
      const { brandImageUrl, skuImageUrl, description, price, brandLineName, hasPriceChanged, productName } = product

      skusWithChangedAvailability.push({
        brandImageUrl,
        skuImageUrl,
        description,
        productName,
        price,
        brandLineName,
        hasPriceChanged,
        hasAvailabilityChanged: true,
        availableToday: updatedSku.availableToday,
        availableTomorrow: updatedSku.availableTomorrow,
        ...updatedProperties
      })
      oldAvailabilityProducts[index] = { ...oldAvailabilityProducts[index], ...updatedProperties }
      localStorage.setItem('skusWithPreviousAvailability', JSON.stringify([...oldAvailabilityProducts]))
    }
  })

  return skusWithChangedAvailability
}

const getRequiredProperties = (products) => products.map(item => {
  const {
    brandImageUrl,
    skuImageUrl,
    description,
    price,
    brandLineName,
    hasPriceChanged,
    productName
  } = item

  return {
    brandImageUrl,
    skuImageUrl,
    description,
    price,
    brandLineName,
    hasPriceChanged,
    productName
  }
})

/**
 * @param {Array} productsWithChangedPrice
 * @param {Array} productsWithChangedStatusOrPrice
 * @returns {Array}
 * The first if check determines whether we have products with availability change or not,
 * if not then update the list with products with changed prices.
 * If we do have products with availability change then loop through those and find the index of product with both
 * availability and price change and push the same into the list,
 * otherwise add the product with only availability change.
 * There might be a usecase where we have some products with availability change and some with price
 * so "updatedProductsList = [...updatedProductsList, ...productsWithChangedPrice]"
 * is for that usecase only where we are combining both.
 */
export const getSkusWithChangedAvailabilityOrPrice =
  (productsWithChangedPrice, productsWithChangedAvailabilityOrPrice) => {
    let updatedProductsList = []

    if (productsWithChangedAvailabilityOrPrice?.length) {
      productsWithChangedAvailabilityOrPrice.forEach(product => {
        const index = productsWithChangedPrice.findIndex(item => item.bvId === product.partNo)
        if (index >= 0) {
          const data = productsWithChangedPrice.splice(index, 1)
          const { hasPriceChanged, price } = data[0]
          updatedProductsList.push({ ...product, hasPriceChanged, price })
        } else {
          updatedProductsList = [...updatedProductsList, product]
        }
      })
      updatedProductsList = [...updatedProductsList, ...getRequiredProperties(productsWithChangedPrice)]
    } else {
      updatedProductsList = [...getRequiredProperties(productsWithChangedPrice)]
    }

    return updatedProductsList
  }
